import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { callBackbonePostApi } from '../../services/backboneApiService';
import { getUser } from '../../utils/cookie';

import MetaTags from '../../components/MetaTags';
import { Heading2 } from '../../components/Heading';
import FooterMenu from '../../components/Footer/FooterMenu';
import { CustomButton } from '../../components/Buttons';

import FeedbackContentSection from './FeedbackContentSection';
import FeedbackIdentitySection from './FeedbackIdentitySection';
import SessionContextCard from './SessionContextCard';
import {
  FEEDBACK_ACCENT,
  FEEDBACK_SUCCESS,
  FEEDBACK_TYPES,
  IDENTITY_MODES,
  RATING_CATEGORIES,
  SESSION_SUMMARY_CONTEXT_FIELDS,
  SUBMIT_STATUS,
} from './constants';
import {
  buildFeedbackPayload,
  createEmptyRatings,
  createEmptyTexts,
  extractFeedbackErrorMessage,
  getFeedbackIntro,
  getFeedbackPageTitle,
} from './helpers';
import { getTopicLabel, isKnownTopic } from './topics';

const sectionSx = {
  mt: 5,
  pt: 4,
  borderTop: '1px solid rgba(255,255,255,0.12)',
};

const Feedback = () => {
  const { sessionId, topicId } = useParams();
  const location = useLocation();
  const user = getUser();
  const hasAccountSession = !!user?.userId && !!user?.token;
  const username = user?.username || '';
  const submitLockRef = useRef(false);

  const feedbackType = sessionId
    ? FEEDBACK_TYPES.SESSION
    : topicId
      ? FEEDBACK_TYPES.TOPIC
      : FEEDBACK_TYPES.GENERAL;

  const [status, setStatus] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [identityMode, setIdentityMode] = useState(
    hasAccountSession ? IDENTITY_MODES.ACCOUNT : IDENTITY_MODES.USERNAME
  );
  const [guestUsername, setGuestUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [body, setBody] = useState('');
  const [ratings, setRatings] = useState(createEmptyRatings);
  const [texts, setTexts] = useState(createEmptyTexts);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [sessionContextLoading, setSessionContextLoading] = useState(false);
  const [sessionContextError, setSessionContextError] = useState('');

  useEffect(() => {
    setStatus('');
    setSubmitError('');
    setShowSuccessDialog(false);
    setIdentityMode(hasAccountSession ? IDENTITY_MODES.ACCOUNT : IDENTITY_MODES.USERNAME);
    setGuestUsername('');
    setDisplayName('');
    setBody('');
    setRatings(createEmptyRatings());
    setTexts(createEmptyTexts());
    setSessionSummary(null);
    setSessionContextError('');
    submitLockRef.current = false;
  }, [sessionId, topicId, feedbackType, hasAccountSession]);

  useEffect(() => {
    if (!hasAccountSession && identityMode === IDENTITY_MODES.ACCOUNT) {
      setIdentityMode(IDENTITY_MODES.USERNAME);
    }
  }, [hasAccountSession, identityMode]);

  useEffect(() => {
    if (feedbackType === FEEDBACK_TYPES.SESSION && identityMode === IDENTITY_MODES.ANONYMOUS) {
      setIdentityMode(
        hasAccountSession ? IDENTITY_MODES.ACCOUNT : IDENTITY_MODES.USERNAME
      );
    }
  }, [feedbackType, identityMode, hasAccountSession]);

  useEffect(() => {
    if (feedbackType !== FEEDBACK_TYPES.SESSION || !sessionId) {
      return undefined;
    }

    let cancelled = false;
    setSessionContextLoading(true);
    setSessionContextError('');

    callBackbonePostApi(
      {
        action: 'getSessionSummary',
        sessionId,
        fields: SESSION_SUMMARY_CONTEXT_FIELDS,
      },
      '',
      { skipAuth: true }
    )
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response?.status === 200 && response?.data) {
          setSessionSummary(response.data);
        } else {
          setSessionSummary(null);
          setSessionContextError('Workout details unavailable for this session.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionSummary(null);
          setSessionContextError('Could not load workout details.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSessionContextLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [feedbackType, sessionId]);

  const isAnonymous = identityMode === IDENTITY_MODES.ANONYMOUS;
  const skipAuth = identityMode !== IDENTITY_MODES.ACCOUNT;
  const unknownTopic = feedbackType === FEEDBACK_TYPES.TOPIC && !isKnownTopic(topicId);
  const title = getFeedbackPageTitle(feedbackType);
  const intro = getFeedbackIntro(feedbackType, topicId);

  const ownsSession =
    feedbackType === FEEDBACK_TYPES.SESSION &&
    hasAccountSession &&
    sessionSummary?.UserID != null
      ? String(user.userId) === String(sessionSummary.UserID)
      : null;

  const contentReady =
    feedbackType === FEEDBACK_TYPES.SESSION
      ? RATING_CATEGORIES.some(({ key }) => ratings[key] != null && ratings[key] > 0)
      : !!body.trim();

  const identityReady =
    identityMode === IDENTITY_MODES.ANONYMOUS
      ? true
      : identityMode === IDENTITY_MODES.ACCOUNT
        ? hasAccountSession
        : !!guestUsername.trim();

  const isSaving = status === SUBMIT_STATUS.saving;
  const isSaved = status === SUBMIT_STATUS.saved;
  const fieldsDisabled = isSaving;
  const sessionBlocked =
    feedbackType === FEEDBACK_TYPES.SESSION && ownsSession === false && hasAccountSession;

  const clearStatus = () => {
    setStatus('');
    setSubmitError('');
  };

  const onChangeRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
    clearStatus();
  };

  const onChangeText = (key, value) => {
    setTexts((prev) => ({ ...prev, [key]: value }));
    clearStatus();
  };

  const onSubmit = (event) => {
    event?.preventDefault?.();

    if (submitLockRef.current || isSaving) {
      return;
    }

    if (unknownTopic) {
      setSubmitError('This feedback topic is not supported.');
      return;
    }

    if (sessionBlocked) {
      setSubmitError('You can only submit feedback for your own workout session.');
      return;
    }

    if (feedbackType === FEEDBACK_TYPES.SESSION && isAnonymous) {
      setSubmitError('Log in or use your account username to submit session feedback.');
      return;
    }

    if (!contentReady) {
      setSubmitError(
        feedbackType === FEEDBACK_TYPES.SESSION
          ? 'Rate at least one category before submitting.'
          : 'Enter your feedback before submitting.'
      );
      return;
    }

    if (!identityReady) {
      setSubmitError(
        identityMode === IDENTITY_MODES.USERNAME
          ? 'Enter a username or email, or choose anonymous.'
          : 'Please log in to submit as your account.'
      );
      return;
    }

    submitLockRef.current = true;
    setSubmitError('');
    setStatus(SUBMIT_STATUS.saving);

    const payload = buildFeedbackPayload({
      feedbackType,
      identityMode,
      user,
      username,
      guestUsername,
      displayName,
      body,
      ratings,
      texts,
      topicId,
      sessionId,
      isAnonymous,
      pageUrl: window.location.href,
    });

    callBackbonePostApi(payload, '', { skipAuth })
      .then((data) => {
        if (data.status === 200 && data.data?.success) {
          setStatus(SUBMIT_STATUS.saved);
          setShowSuccessDialog(true);
        } else {
          const message =
            data?.data?.errorMessage || data?.data?.message || 'Submit failed! Please try again later.';
          setStatus('');
          setSubmitError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      })
      .catch((error) => {
        const message = extractFeedbackErrorMessage(error);
        setStatus('');
        setSubmitError(message);
        enqueueSnackbar(message, { variant: 'error' });
      })
      .finally(() => {
        submitLockRef.current = false;
      });
  };

  return (
    <Box>
      <MetaTags title={`Black Box VR - ${title}`} />
      <Box position="relative" display="flex" justifyContent="center" mb={3}>
        <Box
          component="form"
          noValidate
          maxWidth={612}
          width="100%"
          mt={8}
          px={2}
          onSubmit={onSubmit}
        >
          <Heading2>{title}</Heading2>

          {feedbackType === FEEDBACK_TYPES.TOPIC && !unknownTopic && (
            <Typography mt={2} sx={{ opacity: 0.9 }}>
              About:{' '}
              <Box component="span" fontWeight={700} color={FEEDBACK_ACCENT}>
                {getTopicLabel(topicId)}
              </Box>
            </Typography>
          )}

          {unknownTopic ? (
            <Typography mt={2} color="error" role="alert">
              This feedback topic is not supported.
            </Typography>
          ) : (
            <Typography mt={2} sx={{ opacity: 0.92, lineHeight: 1.6 }}>
              {intro}
            </Typography>
          )}

          {feedbackType === FEEDBACK_TYPES.SESSION && (
            <SessionContextCard
              sessionId={sessionId}
              summary={sessionSummary}
              loading={sessionContextLoading}
              error={sessionContextError}
              ownsSession={ownsSession}
              hasAccountSession={hasAccountSession}
            />
          )}

          <Box sx={sectionSx}>
            <FeedbackIdentitySection
              feedbackType={feedbackType}
              identityMode={identityMode}
              setIdentityMode={setIdentityMode}
              hasAccountSession={hasAccountSession}
              username={username}
              guestUsername={guestUsername}
              setGuestUsername={setGuestUsername}
              displayName={displayName}
              setDisplayName={setDisplayName}
              isAnonymous={isAnonymous}
              fieldsDisabled={fieldsDisabled}
              clearStatus={clearStatus}
              returnPath={`${location.pathname}${location.search || ''}`}
            />
          </Box>

          <Box sx={sectionSx}>
            <FeedbackContentSection
              feedbackType={feedbackType}
              unknownTopic={unknownTopic}
              ratings={ratings}
              texts={texts}
              body={body}
              setBody={setBody}
              onChangeRating={onChangeRating}
              onChangeText={onChangeText}
              fieldsDisabled={fieldsDisabled || sessionBlocked}
              clearStatus={clearStatus}
            />
          </Box>

          {submitError && (
            <Typography mt={3} color="error" role="alert" id="feedback-submit-error">
              {submitError}
            </Typography>
          )}

          <CustomButton
            type="submit"
            sx={{ mt: 5, mb: 2 }}
            disabled={isSaving || isSaved || unknownTopic || sessionBlocked}
            loading={isSaving}
            aria-describedby={submitError ? 'feedback-submit-error' : undefined}
          >
            {!status
              ? 'Submit Feedback'
              : status === SUBMIT_STATUS.saving
                ? 'Submitting…'
                : 'Submitted!'}
          </CustomButton>

          {isSaved && !showSuccessDialog && (
            <Typography mt={1} fontSize={14} sx={{ opacity: 0.75 }}>
              Edit any field to submit again.
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        aria-labelledby="feedback-success-title"
        aria-describedby="feedback-success-description"
        PaperProps={{
          sx: {
            backgroundColor: '#1b1b1b',
            color: '#fff',
            backgroundImage: 'none',
            minWidth: { xs: '90%', sm: 420 },
          },
        }}
      >
        <DialogTitle id="feedback-success-title" sx={{ fontWeight: 800 }}>
          Feedback submitted
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="feedback-success-description"
            sx={{ color: 'rgba(255,255,255,0.85)' }}
          >
            Thank you for your feedback! We appreciate you taking the time to share with us.
          </DialogContentText>
          <Typography mt={2} fontWeight={700} color={FEEDBACK_SUCCESS}>
            You’re all set.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <CustomButton onClick={() => setShowSuccessDialog(false)} autoFocus>
            OK
          </CustomButton>
        </DialogActions>
      </Dialog>

      <FooterMenu />
    </Box>
  );
};

export default Feedback;
